import { Component, inject, ViewEncapsulation, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TrainingProgressService } from '../../services/training-progress.service';
import { SafeHtmlPipe } from '../../shared/pipes/safe-html.pipe';
import { Icons } from '../../shared/icons';
import { TranslatePipe } from '../../../filters/translate.pipe';

@Component({
  selector: 'app-overview',
  standalone: true,
  imports: [CommonModule, SafeHtmlPipe, TranslatePipe],
  templateUrl: './overview.component.html',
  styleUrls: ['../../training-styles.css'],
  encapsulation: ViewEncapsulation.None
})
export class OverviewComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  readonly trainingProgressService = inject(TrainingProgressService);
  readonly Icons = Icons;

  programme: any = { title: '', modules: [] };
  loading = true;
  error = false;
  selectedProgramVersionCode = '';

  ngOnInit(): void {
    this.selectedProgramVersionCode = this.route.snapshot.queryParamMap.get('programVersionCode') || '';
    const params = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.trainingProgressService.setProgramVersionCode(this.selectedProgramVersionCode);

    Promise.all([
      this.trainingProgressService.loadSummary(params),
      this.trainingProgressService.loadModules(params)
    ])
      .then(async ([summary, modules]) => {
        const detailed = await Promise.all(modules.map(async (m) => {
          const detail = await this.trainingProgressService.loadModuleDetail(m.module.id);
          await this.trainingProgressService.loadSectionViews(m.module.id);
          return { ...(m.module || {}), sections: detail?.sections || [], quiz: detail?.questions || [] };
        }));
        this.programme = {title: summary.programVersionTitle, modules: detailed};
        this.loading = false;
      })
      .catch(() => { this.error = true; this.loading = false; });
  }

  getModuleStatus(moduleId: string): string {
    return this.trainingProgressService.getModuleStatus(moduleId);
  }

  getViewedCount(moduleId: string): number {
    return this.trainingProgressService.getViewedCount(moduleId);
  }

  getModuleScore(moduleId: string): number | null {
    return this.trainingProgressService.getModuleScore(moduleId);
  }

  getProgressPercent(mod: any): number {
    const status = this.getModuleStatus(mod.id);
    if (status === 'passed') return 100;
    const totalSections = this.trainingProgressService.getSectionCount(mod.id) || 1;
    return Math.round(this.getViewedCount(mod.id) / totalSections * 100);
  }

  getActionLabel(moduleId: string): string {
    const status = this.getModuleStatus(moduleId);
    if (status === 'not-started') return 'Training_Learner.Action_Start';
    if (status === 'passed') return 'Training_Learner.Action_Review';
    return 'Training_Learner.Action_Continue';
  }

  navigate(path: string): void {
    const parts = path.startsWith('/') ? path.split('/').filter(Boolean) : [path];
    const queryParams = this.selectedProgramVersionCode ? { programVersionCode: this.selectedProgramVersionCode } : undefined;
    this.router.navigate(['/my-training', ...parts], { queryParams });
  }
}
